import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { TreeSelect, Tree, Typography, Icon, Row, Col, Form, message, Button, Spin } from 'antd';
import { get, includes, merge, isEmpty, flattenDeep } from 'lodash';
import { logUtils, pageUtils } from '../../../../utils/common';
import { updatePages, publishSiteNavigation } from '../../../../actions/pages.actions';
import './Forms.css';

const { Text } = Typography;
const { TreeNode } = Tree;

const Spinner = props => {
    if (props.visible) {
        return (
            <div className="spinner">
                <Spin />
            </div>
        );
    }
    return '';
};

const createPagesOrderTreeNodes = (treeNodeData, onUpClicked, onDownClicked) => {

    const handleUpClicked = () => {
        onUpClicked && (onUpClicked(treeNodeData.key));
    };
    const handleDownClicked = () => {
        onDownClicked && (onDownClicked(treeNodeData.key));
    };
    const canMoveUp = () => {
        return treeNodeData.order === undefined || treeNodeData.order > 0;
    };
    const canMoveDown = () => {
        return treeNodeData.order === undefined || treeNodeData.order+1 < treeNodeData.siblingsCount;
    };
    return (        
        <TreeNode
            key={treeNodeData.key}
            children={treeNodeData.children.map(childNodeData => (createPagesOrderTreeNodes(childNodeData, onUpClicked, onDownClicked)))}
            title={ 
                <div style={{position: "relative"}}>
                    <span className={treeNodeData.showInMenu === false ? "notInMenu" : ""}>
                        {treeNodeData.title}
                        {treeNodeData.showInMenu === false ? <Icon type="eye-invisible" style={{marginLeft:"1rem"}} title="Excluded from menu"/> : null}
                    </span>
                                 
                    <div style={{display:"inline", position:"absolute", right:"0", fontSize:"1.25em"}}>
                        {treeNodeData.order === undefined ? 
                            <Icon type="warning" className="clickable flashing--orange" style={{marginRight:"0.125rem", marginLeft:"0.125rem"}} title="No order set" onClick={handleDownClicked}/> 
                            :  [
                                    (canMoveUp() ? <Icon type="up-circle" className="clickable" style={{marginRight:"0.125rem", marginLeft:"0.125rem"}} title="Move page up" onClick={handleUpClicked}/> : null),
                                    (canMoveDown() ? <Icon type="down-circle" className="clickable" style={{marginRight:"0.125rem", marginLeft:"0.125rem"}} title="Move page down" onClick={handleDownClicked}/> : null)
                               ]   
                        }
                    </div>
                </div> 
            } 
        />
    );
};

const NavigationForm = props => {
    const { getFieldDecorator, setFieldsValue } = props.form;
    const { error, isUpdating, updateSuccess, siteNavUpdating, siteNavSuccess, siteNavError } = props;
    const [processing, setProcessing] = useState(false);
    const [pages, setPages] = useState(props.pages);
    const [pagesOrder, setPagesOrder] = useState();
    const [excludedPages, setExcludedPages] = useState([]);

    
    /*-----------Lifecycle methods-------------*/
    //When : new pages object fetched
    useEffect(() => {        
        if(!processing && pagesOrder !== undefined){
            message.warning('Someone else updated the site-structure. As a result, any changes made to the setting below have been reset.', 5);
        }

        let excludedPagesArr =  flattenDeep(props.pages.map(_getExcludedPagesKeys));
        setExcludedPages(excludedPagesArr);
        setFieldsValue({excludedPages : excludedPagesArr});
        setPages(props.pages);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.pages]);

    //When : new pages object changed
    useEffect(() => {        
        setPagesOrder(
            pages.map(page => (_convertPageToTreeData(page, pages.length)) )
            .sort( (a, b) => (a.order !== undefined ? a.order : Number.MAX_VALUE) - (b.order !== undefined? b.order : Number.MAX_VALUE))
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, excludedPages]);

    //When: Navigation form save done
    useEffect(() => {
        if (isUpdating === false) {
            if (error) {
                message.error('Something went wrong when saving the navigation settings, please try again.');
                setProcessing(false);
            } else {
                if (updateSuccess) {
                    const excludedPaths = props.form.getFieldValue('excludedPages');

                    logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.UPDATE_NAV_EXCLUDED, { paths: excludedPaths });
                    message.success('Save successful.');
                    setProcessing(false);
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpdating, updateSuccess, error]);

    //When: Navigation form publish done
    useEffect(() => {
        if (siteNavUpdating === false) {
            if (siteNavError) {
                message.error('Something went wrong when publishing the navigation settings, please try again.');
                setProcessing(false);
            } else {
                if (siteNavSuccess) {
                    const excludedPaths = props.form.getFieldValue('excludedPages');
                    logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.NAV_EXCLUDED_PUBLISHED, { paths: excludedPaths });

                    message.success('Published successful.');
                    setProcessing(false);
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteNavUpdating, siteNavSuccess, siteNavError]);


    /*-----------Event-driven methods-------------*/
    const onSaveClicked = e => {
        _submitSettingsForm(false);
    };
    const onPublishClicked = e => {
        _submitSettingsForm(true);
    };    
    const onMovePageUpClicked = path => {
        _updatePageOrder(path, -1);
    };
    const onMovePageDownClicked = path => {
        _updatePageOrder(path, 1);
    };
    
    /*-----------Util methods-------------*/   
    const _submitSettingsForm = doPublish => {
        props.form.validateFields((err, values) => {
            if (!err) {
                let payload = {};
                pages.map( page => (_createPagePayload(page)) ).forEach(rootPage => merge(payload, rootPage));

                if (doPublish) {
                    props.publishSiteNavigation(payload);
                } else {
                    props.updatePages(payload);
                }

                setProcessing(true);
            }
        });
    }; 
    const _convertPageToTreeData = (page, siblingsCount) => {
        let treeData = { 
            title: page.title,
            value: page.path,
            key: page.path,
            children: [],
            order: page.order, 
            siblingsCount: siblingsCount, 
            showInMenu: !includes(excludedPages, page.path)
        };

        if (!isEmpty(page.subpages)) {
            for (let subpage in page.subpages) {
                const childTreeData = _convertPageToTreeData(page.subpages[subpage], Object.keys(page.subpages).length);
                if (!isEmpty(childTreeData)) { treeData.children.push(childTreeData); }
            }
            treeData.children.sort( (a, b) => (a.order !== undefined ? a.order : Number.MAX_VALUE) - (b.order !== undefined? b.order : Number.MAX_VALUE));
        }
        return treeData;
    };    
    const _getExcludedPagesKeys = page => {
        let excludedPages = [];

        if(page.showInMenu === false ){
            excludedPages.push(page.path);
        }

        if (!isEmpty(page.subpages)) {
            for (let subpage in page.subpages) {
                excludedPages.push(..._getExcludedPagesKeys(page.subpages[subpage]));
            }
        }

        return excludedPages;
    };
    const _createPagePayload = (page, level = 0) => {
        const subPageKeys = Object.keys(page.subpages);
        let myPayload = { showInMenu: !includes(excludedPages, page.path) };
        if(page.order !== undefined) { myPayload.order = page.order; }

        if (subPageKeys.length > 0) {
            myPayload.subpages = {};
            subPageKeys.forEach(subPageKey => {
                myPayload.subpages[subPageKey] = _createPagePayload(page.subpages[subPageKey], level + 1);
            });
        }
        if (level === 0) {
            myPayload = { [page.id]: myPayload };
        }
        return myPayload;
    };
    const _updatePageOrder = (path, jump) => {
        let pagesArr = pages;
        const rootPath = pageUtils.getRootFormPath(path);
        const pageObjectPath = pageUtils.getObjectStructureFromPath(path);
        const siblingsObjectPath = pageUtils.getSiblingsObjectStructureFromPath(path);
        let rootPage = pagesArr.find(data => (data.path === rootPath));

        const selectedPage = pageUtils.isRootPagePath(path) ? rootPage : get(rootPage, pageObjectPath);
        const siblings = pageUtils.isRootPagePath(path) ? pagesArr : get(rootPage, siblingsObjectPath);
        
        //Update page order
        if(selectedPage.order !== undefined){
            if( (jump < 0 && selectedPage.order > 0) || (jump > 0 && selectedPage.order < Object.keys(siblings).length -1) ){
                selectedPage.order += jump;
            }
        }else{
            selectedPage.order = Object.keys(siblings).filter(key => siblings[key].order !== undefined).length;
        }
        Object.keys(siblings).find(key => {
            let affectedPage = siblings[key];
            if(affectedPage.path !== selectedPage.path && affectedPage.order === selectedPage.order){
                affectedPage.order-=jump;
                return true;
            }
            return false;
        });
        
        setPages([...pagesArr]);
    }

    return (
        <div className="settingsBlock" data-title="Navigation menu">
            <Form layout="vertical">
                <Row gutter={16} style={{ display: 'flex' }}>
                    <Col span={14}>
                        <Form.Item label="Excluded pages:">
                            {getFieldDecorator('excludedPages')(
                                <TreeSelect
                                    name="excludedPages"
                                    placeholder="Select the excluded pages"
                                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                    treeData={pagesOrder} onChange={setExcludedPages}
                                    multiple allowClear showSearch treeDefaultExpandAll treeNodeFilterProp="title" />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={10} style={{ marginTop: '1.5rem', marginBottom: 'auto' }}>
                        <Text type="secondary">Use this menu to configure the site&apos;s navigation menu. Adding a page here will exclude it from the navigation menu.</Text>
                    </Col>
                </Row>
                <Row gutter={16} style={{ display: 'flex' }}>
                    <Col span={14}>
                        <Form.Item label="Order:">
                            <Tree name="pagesOrder"
                                style={ {background: "white", borderRadius: "5px", border: "1px solid #d9d9d9", maxHeight:"20rem", minHeight:"5rem", overflow:"auto"}}
                                blockNode={true} selectable={false} checkable={false} defaultExpandAll={true} >
                                    {!pagesOrder ? null : pagesOrder.map(
                                        treeNodeData => (
                                            createPagesOrderTreeNodes(treeNodeData, onMovePageUpClicked, onMovePageDownClicked)
                                        )
                                    )}
                            </Tree>
                        </Form.Item>
                    </Col>
                    <Col span={10} style={{ marginTop: '1.5rem', marginBottom: 'auto' }}>
                        <Text type="secondary">Use this menu to configure the order of the site&apos;s navigation menu.</Text>
                    </Col>
                </Row>
                <div className="savePublishGroup">
                    <Button.Group>
                        <Button size="small" type="primary" htmlType="button" value={false} icon="save" title="Save" disabled={processing} onClick={onSaveClicked}/>
                        <Button size="small" type="success" htmlType="button" value={true} icon="cloud-upload" title="Publish" disabled={processing} onClick={onPublishClicked}/>
                    </Button.Group>
                </div>
            </Form>
            <Spinner visible={processing} />
        </div>
    );
};

const mapStateToProps = state => ({
    pages: state.pages.pages,
    isUpdating: state.pages.isUpdating,
    updateSuccess: state.pages.updateSuccess,

    siteNavUpdating: state.pages.siteNavUpdating,
    siteNavSuccess: state.pages.siteNavSuccess,
    siteNavError: state.pages.siteNavError,

    error: state.pages.error
});
const mapDispatchToProps = {
    updatePages,
    publishSiteNavigation
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Form.create({ name: 'navigation_form' })(NavigationForm));
